// src/services/fmpService.js - Updated with enhanced rate limit detection

import { apiCache } from "../utils/cacheService";
import { apiUsageTracker } from "../utils/apiUsageTracker";

// API Configuration
export const FMP_BASE_URL = "https://financialmodelingprep.com/api/v3";
export const FMP_API_KEY =
  process.env.REACT_APP_FMP_API_KEY || "YOUR_API_KEY_HERE";

// Error types
export const API_ERROR_TYPES = {
  RATE_LIMIT: "RATE_LIMIT",
  GENERAL: "GENERAL",
};

// Rate limit detection - stores state of API rate limit detection
const rateLimitState = {
  isRateLimited: false,
  errorMessage: null,
};

/**
 * Helper function for making API requests to Financial Modeling Prep
 * Includes caching and rate limit detection
 */
const fetchFMP = async (endpoint, params = {}) => {
  // Check if we're already rate limited
  if (rateLimitState.isRateLimited) {
    throw {
      type: API_ERROR_TYPES.RATE_LIMIT,
      message:
        rateLimitState.errorMessage ||
        "API rate limit exceeded. Please try again later.",
    };
  }

  // Generate cache key from endpoint and params
  const paramString = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join("&");

  const cacheKey = `${endpoint}?${paramString}`;

  // Check cache first
  const cachedData = apiCache.get(cacheKey);
  if (cachedData) {
    console.log(`Using cached data for ${cacheKey}`);
    return cachedData;
  }

  // Proceed with fetch if not cached
  const url = new URL(`${FMP_BASE_URL}${endpoint}`);

  // Add API key to all requests
  url.searchParams.append("apikey", FMP_API_KEY);

  // Add additional params
  Object.keys(params).forEach((key) => {
    url.searchParams.append(key, params[key]);
  });

  try {
    console.log(
      `Fetching data from ${url.toString().replace(FMP_API_KEY, "API_KEY")}`
    );
    const response = await fetch(url);

    // Check specifically for rate limit error (429)
    if (response.status === 429) {
      const errorMessage =
        "Daily API call limit reached. Please try again in a few minutes.";
      console.error(`API Error: ${errorMessage}`);

      // Set the rate limited state globally
      rateLimitState.isRateLimited = true;
      rateLimitState.errorMessage = errorMessage;

      // Throw a specialized error
      throw {
        type: API_ERROR_TYPES.RATE_LIMIT,
        message: errorMessage,
        status: 429,
      };
    }

    if (!response.ok) {
      throw {
        type: API_ERROR_TYPES.GENERAL,
        message: `API Error: ${response.status} - ${response.statusText}`,
        status: response.status,
      };
    }

    const data = await response.json();

    // Record API call usage
    apiUsageTracker.recordApiCall();

    // Cache the result
    apiCache.set(cacheKey, data);

    return data;
  } catch (error) {
    // If the error is already handled (has a type), just rethrow it
    if (error.type) {
      throw error;
    }

    // Otherwise, convert to our error format
    console.error(`FMP API Error for ${endpoint}:`, error);
    throw {
      type: API_ERROR_TYPES.GENERAL,
      message: `Error accessing financial data: ${error.message}`,
      originalError: error,
    };
  }
};

/**
 * Check if the API is currently rate limited
 * @returns {boolean} True if currently rate limited
 */
export const isRateLimited = () => {
  return rateLimitState.isRateLimited;
};

/**
 * Get the current rate limit error message
 * @returns {string|null} Error message or null if not rate limited
 */
export const getRateLimitMessage = () => {
  return rateLimitState.errorMessage;
};

/**
 * Reset the rate limit state (useful for testing or when day changes)
 */
export const resetRateLimitState = () => {
  rateLimitState.isRateLimited = false;
  rateLimitState.errorMessage = null;
};

// Rest of the file with all the API endpoint functions...
// (Include all your existing functions from the earlier implementation)

/**
 * Get company profile information
 * @param {string} symbol - Stock ticker symbol
 * @returns {Promise<Object[]>} Company profile data
 */
export const getStockProfile = async (symbol) => {
  return fetchFMP(`/profile/${symbol}`);
};

/**
 * Get key metrics for a company
 * @param {string} symbol - Stock ticker symbol
 * @param {string} period - 'annual' or 'quarter'
 * @returns {Promise<Object[]>} Key metrics data
 */
export const getKeyMetrics = async (symbol, period = "annual") => {
  return fetchFMP(`/key-metrics/${symbol}`, { period });
};

/**
 * Get key financial ratios
 * @param {string} symbol - Stock ticker symbol
 * @param {string} period - 'annual' or 'quarter'
 * @returns {Promise<Object[]>} Financial ratios data
 */
export const getFinancialRatios = async (symbol, period = "annual") => {
  return fetchFMP(`/ratios/${symbol}`, { period });
};

/**
 * Get historical price data
 * @param {string} symbol - Stock ticker symbol
 * @param {string} from - Start date (YYYY-MM-DD)
 * @param {string} to - End date (YYYY-MM-DD)
 * @returns {Promise<Object>} Historical price data
 */
export const getHistoricalPrices = async (symbol, from, to) => {
  return fetchFMP(`/historical-price-full/${symbol}`, { from, to });
};

/**
 * Get company income statements
 * @param {string} symbol - Stock ticker symbol
 * @param {string} period - 'annual' or 'quarter'
 * @param {number} limit - Number of statements to retrieve
 * @returns {Promise<Object[]>} Income statement data
 */
export const getIncomeStatement = async (
  symbol,
  period = "annual",
  limit = 5
) => {
  return fetchFMP(`/income-statement/${symbol}`, { period, limit });
};

/**
 * Get list of stocks
 * @returns {Promise<Object[]>} List of available stocks
 */
export const getStockList = async () => {
  return fetchFMP("/stock/list");
};

/**
 * Get sector performance data
 * @returns {Promise<Object[]>} Sector performance data
 */
export const getSectorPerformance = async () => {
  return fetchFMP("/sector-performance");
};

/**
 * Get real-time stock quote
 * @param {string} symbol - Stock ticker symbol
 * @returns {Promise<Object[]>} Real-time quote data
 */
export const getQuote = async (symbol) => {
  return fetchFMP(`/quote/${symbol}`);
};

/**
 * Get company's trailing twelve months ratios
 * @param {string} symbol - Stock ticker symbol
 * @returns {Promise<Object[]>} TTM ratios
 */
export const getRatiosTTM = async (symbol) => {
  return fetchFMP(`/ratios-ttm/${symbol}`);
};

/**
 * Get DCF valuation data
 * @param {string} symbol - Stock ticker symbol
 * @returns {Promise<Object[]>} DCF valuation data
 */
export const getDCFValues = async (symbol) => {
  return fetchFMP(`/discounted-cash-flow/${symbol}`);
};

/**
 * Get insider transactions
 * @param {string} symbol - Stock ticker symbol
 * @param {number} limit - Number of transactions to retrieve
 * @returns {Promise<Object[]>} Insider transactions data
 */
export const getInsiderTransactions = async (symbol, limit = 10) => {
  return fetchFMP(`/insider-trading/${symbol}`, { limit });
};

/**
 * Get company key metrics TTM
 * @param {string} symbol - Stock ticker symbol
 * @returns {Promise<Object[]>} Key metrics TTM data
 */
export const getKeyMetricsTTM = async (symbol) => {
  return fetchFMP(`/key-metrics-ttm/${symbol}`);
};

/**
 * Estimate the number of API calls needed for an operation
 * @param {string} operation - Type of operation
 * @returns {number} Estimated number of API calls
 */
export const estimateApiCalls = (operation) => {
  const estimates = {
    initialLoad: 10,
    stockDetails: 6,
    sectorAnalysis: 3,
  };

  return estimates[operation] || 1;
};

/**
 * Check if there are enough API calls available for an operation
 * @param {string} operation - Type of operation
 * @returns {boolean} True if enough calls available
 */
export const hasEnoughApiCalls = (operation) => {
  // If we're already rate limited, no calls are available
  if (rateLimitState.isRateLimited) {
    return false;
  }

  const requiredCalls = estimateApiCalls(operation);
  return apiUsageTracker.hasEnoughCalls(requiredCalls);
};

/**
 * Get current API usage statistics
 * @returns {Object} API usage stats
 */
export const getApiUsageStats = () => {
  return {
    callsUsedToday: apiUsageTracker.getCallsUsedToday(),
    callsRemaining: apiUsageTracker.getCallsRemaining(),
    usagePercentage: apiUsageTracker.getUsagePercentage(),
    dailyLimit: apiUsageTracker.dailyLimit,
    isRateLimited: rateLimitState.isRateLimited,
  };
};
